import type { SpeedGasMetrics } from '@/lib/api/speed';

const BIG_BLOCK_GAS_LIMIT = 10_000_000n;
const RETENTION_MS = 30 * 60_000;
const MIN_WINDOW_SAMPLES = 5;

type ActiveMetrics = Extract<SpeedGasMetrics, { enabled: true }>;

function rounded(value: number): number {
    return Math.round(value * 10_000) / 10_000;
}

export class ChainLoadSampler {
    private samples: Array<{ at: number; load: number }> = [];
    private lastBlock: bigint | null = null;

    push(
        now: number,
        blockNumber: bigint,
        gasUsed: bigint,
        gasLimit: bigint,
    ): ActiveMetrics | null {
        if (
            blockNumber === this.lastBlock ||
            gasLimit <= 0n ||
            gasLimit > BIG_BLOCK_GAS_LIMIT
        ) {
            return null;
        }
        this.lastBlock = blockNumber;
        const current = rounded(Number(gasUsed) / Number(gasLimit));
        this.samples.push({ at: now, load: current });
        const cutoff = now - RETENTION_MS;
        this.samples = this.samples.filter((sample) => sample.at >= cutoff);
        return {
            enabled: true,
            networkId: 999,
            blockNumber: Number(blockNumber),
            gasLimit: Number(gasLimit),
            current,
            avg1m: this.average(now, 60_000),
            avg5m: this.average(now, 5 * 60_000),
            avg15m: this.average(now, 15 * 60_000),
            avg30m: this.average(now, 30 * 60_000),
            updatedAt: new Date(now).toISOString(),
        };
    }

    size(): number {
        return this.samples.length;
    }

    private average(now: number, windowMs: number): number | null {
        const samples = this.samples.filter(
            (sample) => sample.at >= now - windowMs,
        );
        if (samples.length < MIN_WINDOW_SAMPLES) return null;
        return rounded(
            samples.reduce((sum, sample) => sum + sample.load, 0) /
                samples.length,
        );
    }
}
